import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";
import { DatePicker, Button, Table } from "antd";
import dayjs from "dayjs";
import axios from "../../utils/axiosDash";

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────
const C = {
  red:         "#E24B4A",
  redLight:    "#FCEBEB",
  amber:       "#BA7517",
  amberLight:  "#FAEEDA",
  amberMid:    "#FAC775",
  green:       "#3B6D11",
  greenLight:  "#EAF3DE",
  greenMid:    "#97C459",
  blue:        "#185FA5",
  blueLight:   "#E6F1FB",
  blueMid:     "#378ADD",
  teal:        "#0F6E56",
  tealLight:   "#E1F5EE",
  purple:      "#534AB7",
  purpleLight: "#EFEDFB",
  gray:        "#888780",
  grayLight:   "#F1EFE8",
  dark:        "#2C2C2A",
  border:      "rgba(0,0,0,0.08)",
};

const PALETTE = [C.blueMid, C.red, C.amberMid, C.greenMid, C.purple, C.teal];

// ── DATA PROCESSING ──────────────────────────────────────────────────────
function processPersonData(rawData) {
  if (!rawData?.length) {
    return {
      cameraStats: [],
      timeSeries: [],
      densityDistribution: [],
      cameraIds: [],
      totalEvents: 0,
      totalPersons: 0,
      avgPersons: 0,
      alertCount: 0,
      byHour: {},
      byCamera: {},
      busiestCamera: null,
    };
  }

  const rows = rawData.map(item => ({
    ...item,
    _time: item.timestamp || new Date().toISOString(),
    _personCount: typeof item.newPersons === 'number' ? item.newPersons : 0,
    _isAlert: item.alert === true,
  }));

  const sorted = [...rows].sort((a, b) => new Date(a._time) - new Date(b._time));

  // Camera statistics
  const cameraMap = {};
  rows.forEach(r => {
    const cam = r.camera_id || "unknown";
    if (!cameraMap[cam]) {
      cameraMap[cam] = {
        camera: cam,
        cameraName: r.camera_name || cam,
        totalEvents: 0,
        totalPersons: 0,
        alertCount: 0,
        avgPersons: 0,
      };
    }
    const entry = cameraMap[cam];
    entry.totalEvents++;
    entry.totalPersons += r._personCount;
    if (r._isAlert) entry.alertCount++;
    entry.avgPersons = entry.totalEvents > 0 ? Math.round(entry.totalPersons / entry.totalEvents) : 0;
  });

  const cameraStats = Object.values(cameraMap).sort((a, b) => b.totalEvents - a.totalEvents);

  // Find busiest camera (highest totalPersons)
  const busiestCamera = cameraStats.length > 0 
    ? cameraStats.reduce((max, cam) => cam.totalPersons > max.totalPersons ? cam : max, cameraStats[0])
    : null;

  // Time series (hourly aggregation) - using timestampString
  const hourMap = {};
  sorted.forEach(r => {
    let hour;
    try {
      if (r.timestampString) {
        const parts = r.timestampString.split(' ');
        if (parts.length > 1) {
          const timeParts = parts[1].split(':');
          hour = `${timeParts[0]}:00`;
        } else {
          hour = dayjs(r._time).format("HH:00");
        }
      } else {
        hour = dayjs(r._time).format("HH:00");
      }
    } catch (e) {
      hour = dayjs(r._time).format("HH:00");
    }
    
    if (!hourMap[hour]) {
      hourMap[hour] = { time: hour, count: 0, persons: 0, alerts: 0 };
    }
    hourMap[hour].count++;
    hourMap[hour].persons += r._personCount;
    if (r._isAlert) hourMap[hour].alerts++;
  });
  
  // Get all 24 hours and fill missing ones
  const allHours = [];
  for (let i = 0; i < 24; i++) {
    const hourStr = `${String(i).padStart(2, '0')}:00`;
    allHours.push(hourStr);
  }
  
  const timeSeries = allHours.map(h => ({
    time: h,
    count: hourMap[h]?.count || 0,
    persons: hourMap[h]?.persons || 0,
    alerts: hourMap[h]?.alerts || 0,
  }));

  // Density distribution (categorize by newPersons count)
  const densityMap = {
    "Empty (0)": 0,
    "Low (1-3)": 0,
    "Medium (4-6)": 0,
    "High (7-10)": 0,
    "Very High (10+)": 0,
  };
  rows.forEach(r => {
    const count = r._personCount;
    if (count === 0) densityMap["Empty (0)"]++;
    else if (count <= 3) densityMap["Low (1-3)"]++;
    else if (count <= 6) densityMap["Medium (4-6)"]++;
    else if (count <= 10) densityMap["High (7-10)"]++;
    else densityMap["Very High (10+)"]++;
  });

  const densityDistribution = Object.entries(densityMap)
    .map(([name, value]) => ({ name, value }))
    .filter(d => d.value > 0);

  // Stats
  const totalEvents = rows.length;
  const totalPersons = rows.reduce((sum, r) => sum + r._personCount, 0);
  const avgPersons = totalEvents > 0 ? Math.round(totalPersons / totalEvents) : 0;
  const alertCount = rows.filter(r => r._isAlert).length;

  return {
    cameraStats,
    timeSeries,
    densityDistribution,
    cameraIds: cameraStats.map(c => c.camera),
    totalEvents,
    totalPersons,
    avgPersons,
    alertCount,
    byHour: hourMap,
    byCamera: cameraMap,
    busiestCamera,
  };
}

// ── CHART TOOLTIP ──────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label, unit = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-xl px-3.5 py-2.5 text-xs shadow-lg" style={{ borderColor: C.border }}>
      <p className="font-bold text-slate-900 mb-1.5 text-[11px]">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-semibold my-0.5" style={{ color: p.color || p.fill }}>
          {p.name}: <span className="text-slate-900">{p.value}{unit}</span>
        </p>
      ))}
    </div>
  );
};

// ── KPI CARD ───────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, color, bg, icon }) => (
  <div className="relative flex-1 min-w-[160px] rounded-2xl p-5" style={{ background: bg || "#fff", border: `1px solid ${color}22` }}>
    <div className="absolute top-0 right-0 w-[72px] h-[72px] rounded-tr-2xl" style={{ background: `${color}12`, borderRadius: "0 16px 0 72px" }} />
    <div className="flex items-center gap-2 mb-1">
      {icon && <span className="text-xl">{icon}</span>}
      <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-500">{label}</div>
    </div>
    <div className="text-[38px] font-extrabold leading-none mb-1.5" style={{ color }}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

// ── DENSITY BADGE ──────────────────────────────────────────────────────
const DensityBadge = ({ count }) => {
  let color, bg, label;
  if (count === 0) {
    color = C.gray;
    bg = C.grayLight;
    label = "Empty";
  } else if (count <= 3) {
    color = C.green;
    bg = C.greenLight;
    label = "Low";
  } else if (count <= 6) {
    color = C.amber;
    bg = C.amberLight;
    label = "Medium";
  } else if (count <= 10) {
    color = C.amber;
    bg = C.amberLight;
    label = "High";
  } else {
    color = C.red;
    bg = C.redLight;
    label = "Critical";
  }
  
  return (
    <span className="px-3 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap" style={{ background: bg, color }}>
      {label} ({count})
    </span>
  );
};

// ── PANEL ──────────────────────────────────────────────────────────────
const Panel = ({ children, className = "", style = {} }) => (
  <div className={`bg-white border rounded-2xl p-5 ${className}`} style={{ borderColor: C.border, ...style }}>
    {children}
  </div>
);

const SectionHead = ({ title, sub }) => (
  <div className="mb-4">
    <div className="font-extrabold text-sm text-slate-900">{title}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────
export default function Reports() {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const d = useMemo(() => processPersonData(rawData), [rawData]);

  const fetchData = async (date) => {
    try {
      setLoading(true);
      const res = await axios.post("/fetch-tanseeq-reports", {
        filterDate: (date || selectedDate).format("YYYY-MM-DD"),
      });
      if (res.data.success) setRawData(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(selectedDate); }, [selectedDate]);

  const axisProps = { tick: { fontSize: 10, fill: C.gray }, tickLine: false, axisLine: false };

  // Camera columns for table - UPDATED: Removed Total Events and Total People
  const cameraColumns = [
    {
      title: "Camera", dataIndex: "camera", key: "camera",
      render: (v, row) => (
        <div>
          <div className="font-bold text-slate-900 text-[13px]">{row.cameraName || v}</div>
          <div className="text-[11px] text-slate-500">ID: {v}</div>
        </div>
      ),
    },
    {
      title: "Avg People", dataIndex: "avgPersons", key: "avgPersons",
      render: v => <span className="font-semibold text-teal-600">{v}</span>,
      sorter: (a, b) => a.avgPersons - b.avgPersons,
    },
    {
      title: "Alerts", dataIndex: "alertCount", key: "alertCount",
      render: v => <span className="font-bold text-red-500">{v}</span>,
      sorter: (a, b) => a.alertCount - b.alertCount,
    },
  ];

  const TABS = [
    { key: "overview", label: "Overview" },
    { key: "cameras", label: "Camera Analytics" },
    { key: "stats", label: "Statistics" },
  ];

  return (
    <div className="p-7 min-h-screen font-sans">
      <div className="max-w-[1560px] mx-auto">

        {/* HEADER */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_0_3px_#EAF3DE]" />
              <span className="text-[11px] font-bold uppercase tracking-[1.2px] text-teal-600">Live Analytics</span>
            </div>
            <h1 className="text-[28px] font-bold text-slate-900 tracking-[-0.5px] m-0">Video Surveillance Reports</h1>
            <p className="text-[13px] text-slate-500 mt-1">Real-time reports across all surveillance cameras</p>
          </div>
          <div className="flex gap-2.5 items-center">
            <DatePicker
              value={selectedDate}
              onChange={val => setSelectedDate(val || dayjs())}
              format="MMM D, YYYY"
              allowClear={false}
              className="h-10 rounded-[40px]"
            />
            <Button
              onClick={() => fetchData(selectedDate)}
              loading={loading}
              className="h-10 rounded-[40px] font-bold border-none"
              style={{ background: C.dark, color: "#fff" }}
            >
              ↻ Refresh
            </Button>
          </div>
        </div>

        {/* KPI STRIP - UPDATED: Removed Total Events and Total People, added Busiest Camera */}
        <div className="flex gap-4 flex-wrap mb-7">
          <KpiCard 
            label="Total Alerts" 
            value={d.alertCount} 
            sub="Critical events" 
            color={C.red} 
            bg={C.redLight}
            icon="⚠️"
          />
          <KpiCard 
            label="Active Cameras" 
            value={d.cameraIds.length} 
            sub="With detections" 
            color={C.teal} 
            bg={C.tealLight}
            icon="📹"
          />
          <KpiCard 
            label="Busiest Camera" 
            value={d.busiestCamera?.cameraName || '—'}
            color={C.blue} 
            bg={C.blueLight}
            icon="📹"
          />
        </div>

        {/* TABS CONTAINER */}
        <div className="bg-white border rounded-2xl overflow-hidden" style={{ borderColor: C.border }}>
          <div className="border-b px-6" style={{ borderColor: C.border }}>
            <div className="flex">
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-5 py-4 border-none text-[13px] font-medium transition-all duration-150 ${
                    activeTab === t.key 
                      ? 'font-extrabold text-slate-900 border-b-2 border-slate-900' 
                      : 'text-slate-500 border-b-2 border-transparent'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">

            {/* ── OVERVIEW ── */}
            {activeTab === "overview" && (
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6">

                  {/* Person count over time */}
                  <Panel>
                    <SectionHead title="Person Detection Trend" sub="People detected over time" />
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={d.timeSeries} barCategoryGap="35%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
                        <XAxis dataKey="time" {...axisProps} />
                        <YAxis {...axisProps} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip unit=" people" />} />
                        <Bar dataKey="persons" name="People" fill={C.blueMid} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>

                  {/* Density distribution based on newPersons */}
                  <Panel>
                    <SectionHead title="Person Density" sub="Distribution of person counts" />
                    <ResponsiveContainer width="100%" height={190}>
                      <PieChart>
                        <Pie
                          data={d.densityDistribution} cx="50%" cy="50%"
                          innerRadius={50} outerRadius={78} paddingAngle={3}
                          dataKey="value"
                          label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {d.densityDistribution.map((e, i) => (
                            <Cell key={i} fill={[C.green, C.blueMid, C.amber, C.red, C.dark][i % 5]} stroke="white" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip content={<ChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="border-t pt-3 mt-1" style={{ borderColor: C.border }}>
                      {d.densityDistribution.map(z => (
                        <div key={z.name} className="flex justify-between items-center py-1">
                          <span className="text-xs text-slate-900">{z.name}</span>
                          <span className="font-extrabold text-sm text-slate-900">{z.value}</span>
                        </div>
                      ))}
                    </div>
                  </Panel>
                </div>

                {/* Alert trend */}
                {d.timeSeries.some(t => t.alerts > 0) && (
                  <Panel>
                    <SectionHead title="Alert Activity" sub="Critical alerts over time" />
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={d.timeSeries}>
                        <defs>
                          <linearGradient id="alertGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={C.red} stopOpacity={0.2} />
                            <stop offset="95%" stopColor={C.red} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
                        <XAxis dataKey="time" {...axisProps} />
                        <YAxis {...axisProps} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Area type="monotone" dataKey="alerts" name="Alerts" stroke={C.red} strokeWidth={2} fill="url(#alertGrad)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Panel>
                )}
              </div>
            )}

            {/* ── CAMERA ANALYTICS ── */}
            {activeTab === "cameras" && (
              <div className="flex flex-col gap-6">
                <Panel>
                  <SectionHead title="Camera Activity" sub="Total person detections per camera" />
                  <ResponsiveContainer width="100%" height={Math.max(d.cameraStats.length * 52, 240)}>
                    <BarChart data={d.cameraStats} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" horizontal={false} />
                      <XAxis type="number" {...axisProps} allowDecimals={false} />
                      <YAxis type="category" dataKey="cameraName" tick={{ fontSize: 12, fontWeight: 600, fill: C.dark }} width={120} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="totalEvents" name="Total Events" radius={[0, 8, 8, 0]}>
                        {d.cameraStats.map((e, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>

                <Panel>
                  <SectionHead title="Camera Rankings" sub="By total events and alerts count" />
                  {d.cameraStats.length === 0 && (
                    <div className="text-center text-slate-500 py-10 text-[13px]">No data for selected date</div>
                  )}
                  {d.cameraStats.map((cam, i) => {
                    const max = Math.max(...d.cameraStats.map(c => c.totalEvents), 1);
                    const pct = (cam.totalEvents / max) * 100;
                    const barColor = PALETTE[i % PALETTE.length];
                    return (
                      <div key={cam.camera} className={`mb-5 pb-5 ${i < d.cameraStats.length - 1 ? `border-b` : ''}`} style={{ borderColor: C.border }}>
                        <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                          <div className="flex items-center gap-2.5">
                            <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-extrabold flex-shrink-0" style={{ background: `${barColor}18`, color: barColor }}>
                              #{i + 1}
                            </span>
                            <span className="font-bold text-[13px] text-slate-900">{cam.cameraName || cam.camera}</span>
                          </div>
                          <div className="flex items-center gap-3.5 flex-wrap">
                            {/* UPDATED: Removed the "Total: X" text */}
                            {cam.alertCount > 0 && (
                              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold" style={{ background: C.redLight, color: C.red }}>
                                ⚠️ {cam.alertCount} alerts
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="h-1.5 rounded-sm overflow-hidden" style={{ background: C.grayLight }}>
                          <div className="h-full rounded-sm transition-all duration-300" style={{ width: `${pct}%`, background: barColor }} />
                        </div>
                      </div>
                    );
                  })}
                </Panel>
              </div>
            )}

            {/* ── STATISTICS TABLE ── */}
            {activeTab === "stats" && (
              <div>
                <SectionHead title="Camera Statistics" sub="Detailed metrics per camera based on person detections" />
                <Panel className="p-0 overflow-hidden">
                  <Table
                    dataSource={d.cameraStats}
                    columns={cameraColumns}
                    rowKey="camera"
                    pagination={{ pageSize: 10, size: "small" }}
                    size="middle"
                  />
                </Panel>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}